import { Model } from 'jms-domain'

import { isCreateEntityUpdate } from '@salsita-ai/platform-project-sdk/domain'

import { configuratorInterface } from '../systems/configurator-interface.js'

/**
 * Assign a hood to a cooking item - place it above it and size it properly.
 */
export const moveEndPanelTransformer = configuratorInterface.transformerBuilder.when
  .update(['createDomainEntity'])
  .fired.and(ctx => Model.BottomWallItem.isId(ctx.update.entityId) && !Model.EndPanel.isId(ctx.update.entityId))
  .then.applySideEffect(ctx => {
    const $newRoot = ctx.getNew$Root()

    const $newWallItem = $newRoot.$bottomWallItems.find(c => c.id === ctx.update.entityId)
    if (!$newWallItem) {
      throw new Error(`New wall item not found: ${ctx.update.entityId}`)
    }

    const $wall = $newRoot.$walls.find(w => w.id === $newWallItem.wallId)
    if (!$wall) {
      throw new Error(`Wall not found: ${$newWallItem.wallId}`)
    }

    // Skip when there are only two items, including the new one.
    // There's nothing to move. The first item could be an end panel.
    if ($wall.$bottomItems.length <= 2) {
      return []
    }

    // Skip when there are EndPanels in the list of new items.
    // This could mean it's a batch of items, which are already ordered correctly.
    const numberOfEndPanelsAdded = ctx.updates.filter(u => isCreateEntityUpdate(u, [Model.EndPanel.type])).length
    if (numberOfEndPanelsAdded > 0) {
      return []
    }

    if ($wall.$direction === Model.WallDirection.left) {
      const $wallItems = [...$wall.$bottomItems].sort((a, b) => a.x - b.x)
      const indexOfNewItem = $wallItems.findIndex(c => c.id === ctx.update.entityId)
      const $previousWallItem = $wallItems[indexOfNewItem + 1]

      if ($previousWallItem && Model.EndPanel.isId($previousWallItem.id)) {
        const $endPanel = $previousWallItem
        const wallItemX = $endPanel.x + $endPanel.$width - $newWallItem.$width
        const endPanelX = wallItemX - $endPanel.$width

        return [
          configuratorInterface.updates.DomainEntityModification({
            entityId: $newWallItem.id,
            stateDelta: {
              x: wallItemX,
            },
            updateTag: 'moveEndPanel',
          }),
          configuratorInterface.updates.DomainEntityModification({
            entityId: $endPanel.id,
            stateDelta: {
              x: endPanelX,
            },
            updateTag: 'moveEndPanel',
          }),
        ]
      }
    } else {
      const $wallItems = [...$wall.$bottomItems].sort((a, b) => b.x - a.x)
      const indexOfNewItem = $wallItems.findIndex(c => c.id === ctx.update.entityId)
      const $previousWallItem = $wallItems[indexOfNewItem + 1]

      if (!$previousWallItem || !Model.BottomWallItem.isId($previousWallItem.id)) {
        return []
      }

      if ($previousWallItem && Model.EndPanel.isId($previousWallItem.id)) {
        const $endPanel = $previousWallItem
        const wallItemX = $endPanel.x
        const endPanelX = wallItemX + $newWallItem.$width

        return [
          configuratorInterface.updates.DomainEntityModification({
            entityId: $newWallItem.id,
            stateDelta: {
              x: wallItemX,
            },
            updateTag: 'moveEndPanel',
          }),
          configuratorInterface.updates.DomainEntityModification({
            entityId: $endPanel.id,
            stateDelta: {
              x: endPanelX,
            },
            updateTag: 'moveEndPanel',
          }),
        ]
      }
    }

    return []
  })
  .build('Move end panel after new wall item')
